import { BRAND } from './constants';
import { brl, numberBR, pctBR } from './format';

export function decimalBR(value, decimals) {
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formata o valor cru de um KPI de acordo com um formato — decidido pelo
 * componente que monta o card (`format` nunca vem do mock).
 */
export function formatKpiValue(value, format) {
  switch (format) {
    case 'int': return numberBR(value);
    case 'decimal1': return decimalBR(value, 1);
    case 'decimal2': return decimalBR(value, 2);
    case 'percent2': return pctBR(value, 2);
    case 'currency': return brl(value);
    case 'currencyMil': return `R$ ${Math.round(value / 1000)} mil`;
    case 'currencyMi': return `R$ ${decimalBR(value / 1000000, 2)} mi`;
    case 'daysDecimal1': return `${decimalBR(value, 1)} dias`;
    default: return String(value);
  }
}

/**
 * Monta o texto de uma variação (mês/ano) a partir dos números crus do mock
 * (`value`/`pct` — campos soltos, ex.: `deltaMonthValue`/`deltaMonthPct`) e
 * do formato/estilo decididos pelo componente. `style === 'pctOnly'` é para
 * KPIs cujo próprio valor já é um percentual — a variação é o ponto
 * percentual em si, sem o "(±x%)" relativo (ex.: "-8,34%" em vez de
 * "-8,34 (-12,3%)").
 */
export function formatKpiDelta(value, pct, format, style) {
  if (value === undefined || value === null) return '';

  const sign = value >= 0 ? '+' : '-';
  const absText = formatKpiValue(Math.abs(value), format);

  if (style === 'pctOnly') {
    return `${sign}${absText}`;
  }

  const pctSign = pct >= 0 ? '+' : '-';
  return `${sign}${absText} (${pctSign}${pctBR(Math.abs(pct))})`;
}

/**
 * Cor da variação — decidida inteiramente pelo componente, nunca pelo mock.
 * Por padrão, valor positivo é bom (verde) e negativo é ruim (vermelho);
 * `style.goodDirection: 'down'` inverte essa leitura para KPIs onde diminuir
 * é a melhora (ex.: cotações congeladas, prazo de fechamento). `deltaNeutral`
 * (ambos os lados) ou `deltaMonthNeutral`/`deltaYearNeutral` (um lado só)
 * forçam cinza quando a variação não deve ser julgada (ex.: tamanho do
 * cadastro), independente do sinal.
 */
export function deltaColor(value, style = {}, side) {
  if (value === undefined || value === null) return null;

  const forcedNeutral = style.deltaNeutral
    || (side === 'month' && style.deltaMonthNeutral)
    || (side === 'year' && style.deltaYearNeutral);
  if (forcedNeutral) return BRAND.cinza;

  const isGood = style.goodDirection === 'down' ? value <= 0 : value >= 0;
  return isGood ? BRAND.verde : BRAND.vermelho;
}

/**
 * Converte um array de KPIs crus do mock (`{ key, value, progress,
 * deltaMonthValue, deltaMonthPct, deltaYearValue, deltaYearPct, ...outros
 * números soltos usados só em captions }` — só o `key` identifica de qual
 * KPI se trata, sem nenhum texto de exibição, formato ou cor) nas props
 * finais que `KpiTile` espera, usando `styleByKey` — um dicionário, definido
 * no componente da seção, com label/tooltip/caption/formato/cores de cada
 * KPI pelo seu `key`. É aqui, não no mock, que texto e cor são decididos —
 * o mesmo padrão já usado para montar os gráficos.
 *
 * `style.caption` é um texto fixo (quando o KPI não depende de nenhum
 * número, ex.: "base de itens ativos"); `style.captionTemplate(raw)` monta
 * o texto a partir de números crus complementares soltos em `raw` (ex.:
 * "12,6% do cadastro · mês +0,4%").
 */
export function buildKpiTiles(rawTiles, styleByKey, { valueFontSize } = {}) {
  return rawTiles.map((raw) => {
    const style = styleByKey[raw.key] || {};
    const caption = style.captionTemplate ? style.captionTemplate(raw) : (style.caption || '');
    const tile = {
      label: style.label,
      tooltip: style.tooltip,
      value: formatKpiValue(raw.value, style.format),
      valueColor: style.valueColor || BRAND.preto,
      caption,
      captionColor: style.captionColor || BRAND.muted,
      progress: raw.progress != null ? raw.progress : null,
      progressColor: style.progressColor || BRAND.laranja,
      monthDelta: formatKpiDelta(raw.deltaMonthValue, raw.deltaMonthPct, style.deltaMonthFormat || style.format, style.deltaStyle),
      monthColor: deltaColor(raw.deltaMonthValue, style, 'month') || BRAND.verde,
      yearDelta: formatKpiDelta(raw.deltaYearValue, raw.deltaYearPct, style.deltaYearFormat || style.format, style.deltaStyle),
      yearColor: deltaColor(raw.deltaYearValue, style, 'year') || BRAND.verde,
    };

    if (style.border) tile.borderColor = style.border;
    if (valueFontSize) tile.valueFontSize = valueFontSize;

    return tile;
  });
}
